'use client';
import { MotionGlobalConfig } from 'framer-motion';
import { MotionGlobalConfig as MotionGlobalConfig2 } from 'motion/react';
import React from 'react';

import { useHeaderScreenSize } from '@/hooks/useScreenSizeDecodo';

import AnimatedElement, { AnimatedElementProps } from './AnimatedElement';
import { useAnimationContext } from './AnimationContext';

function setGlobalLibSkipAnimations(boolean: boolean) {
  MotionGlobalConfig.skipAnimations = boolean;
  MotionGlobalConfig2.skipAnimations = boolean;
}

function AnimatedEl<T extends keyof HTMLElements>({
  ...props
}: AnimatedElementProps<T> & {
  children?: React.ReactNode;
  el: T;
  /**
   * Ignore the global config and force animations on a component basis.
   */
  forceAnimation?: {
    desktop?: 'force-enable' | 'force-disable' | undefined;
    mobile?: 'force-enable' | 'force-disable' | undefined;
  };
}) {
  const { el, forceAnimation } = props;
  const { enableAnimation } = useAnimationContext();

  const { isDesktop } = useHeaderScreenSize();
  const isSmallerScreen =
    typeof window !== 'undefined' && window.innerWidth < 1340;

  const enableAnimations = (() => {
    if (isDesktop) {
      if (forceAnimation?.desktop === 'force-enable') {
        return true;
      }
      if (forceAnimation?.desktop === 'force-disable') {
        return false;
      }

      return enableAnimation.desktop;
    }
    if (forceAnimation?.mobile === 'force-enable') {
      return true;
    }
    if (forceAnimation?.mobile === 'force-disable') {
      return false;
    }
    return enableAnimation.mobile;
  })();

  const finalProps = { ...props };

  if (!finalProps.forceAnimation) {
    delete finalProps.forceAnimation;
  }

  if (enableAnimations) {
    setGlobalLibSkipAnimations(false);

    return <AnimatedElement {...finalProps} />;
  }

  setGlobalLibSkipAnimations(true);

  if (!!props.variants) {
    return <AnimatedElement shouldStripAnimations {...finalProps} />;
  }

  if (!isSmallerScreen) {
    // TODO: Sticking to HTML elements on small screens kills the mega menu.
    // for further improvement see if we can use the HTML tags instead here.
    // TS-ignoring for now but keeping for future reference.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const Component = elStringHTMLElementMap[el as keyof HTMLElements];
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return <Component {...props} />;
  }

  return <AnimatedElement shouldStripAnimations {...props} />;
}

export default AnimatedEl;

// FIXME: this was yeeted from the framer-motion types as it wasn't exported. See if there are other places to get the type
export interface HTMLElements {
  a: HTMLAnchorElement;
  abbr: HTMLElement;
  address: HTMLElement;
  area: HTMLAreaElement;
  article: HTMLElement;
  aside: HTMLElement;
  audio: HTMLAudioElement;
  b: HTMLElement;
  base: HTMLBaseElement;
  bdi: HTMLElement;
  bdo: HTMLElement;
  big: HTMLElement;
  blockquote: HTMLQuoteElement;
  body: HTMLBodyElement;
  br: HTMLBRElement;
  button: HTMLButtonElement;
  canvas: HTMLCanvasElement;
  caption: HTMLElement;
  center: HTMLElement;
  cite: HTMLElement;
  code: HTMLElement;
  col: HTMLTableColElement;
  colgroup: HTMLTableColElement;
  data: HTMLDataElement;
  datalist: HTMLDataListElement;
  dd: HTMLElement;
  del: HTMLModElement;
  details: HTMLDetailsElement;
  dfn: HTMLElement;
  dialog: HTMLDialogElement;
  div: HTMLDivElement;
  dl: HTMLDListElement;
  dt: HTMLElement;
  em: HTMLElement;
  embed: HTMLEmbedElement;
  fieldset: HTMLFieldSetElement;
  figcaption: HTMLElement;
  figure: HTMLElement;
  footer: HTMLElement;
  form: HTMLFormElement;
  h1: HTMLHeadingElement;
  h2: HTMLHeadingElement;
  h3: HTMLHeadingElement;
  h4: HTMLHeadingElement;
  h5: HTMLHeadingElement;
  h6: HTMLHeadingElement;
  head: HTMLHeadElement;
  header: HTMLElement;
  hgroup: HTMLElement;
  hr: HTMLHRElement;
  html: HTMLHtmlElement;
  i: HTMLElement;
  iframe: HTMLIFrameElement;
  img: HTMLImageElement;
  input: HTMLInputElement;
  ins: HTMLModElement;
  kbd: HTMLElement;
  keygen: HTMLElement;
  label: HTMLLabelElement;
  legend: HTMLLegendElement;
  li: HTMLLIElement;
  link: HTMLLinkElement;
  main: HTMLElement;
  map: HTMLMapElement;
  mark: HTMLElement;
  menu: HTMLElement;
  menuitem: HTMLElement;
  meta: HTMLMetaElement;
  meter: HTMLMeterElement;
  nav: HTMLElement;
  noindex: HTMLElement;
  noscript: HTMLElement;
  object: HTMLObjectElement;
  ol: HTMLOListElement;
  optgroup: HTMLOptGroupElement;
  option: HTMLOptionElement;
  output: HTMLOutputElement;
  p: HTMLParagraphElement;
  param: HTMLParamElement;
  picture: HTMLElement;
  pre: HTMLPreElement;
  progress: HTMLProgressElement;
  q: HTMLQuoteElement;
  rp: HTMLElement;
  rt: HTMLElement;
  ruby: HTMLElement;
  s: HTMLElement;
  samp: HTMLElement;
  search: HTMLElement;
  slot: HTMLSlotElement;
  script: HTMLScriptElement;
  section: HTMLElement;
  select: HTMLSelectElement;
  small: HTMLElement;
  source: HTMLSourceElement;
  span: HTMLSpanElement;
  strong: HTMLElement;
  style: HTMLStyleElement;
  sub: HTMLElement;
  summary: HTMLElement;
  sup: HTMLElement;
  table: HTMLTableElement;
  template: HTMLTemplateElement;
  tbody: HTMLTableSectionElement;
  td: HTMLTableDataCellElement;
  textarea: HTMLTextAreaElement;
  tfoot: HTMLTableSectionElement;
  th: HTMLTableHeaderCellElement;
  thead: HTMLTableSectionElement;
  time: HTMLTimeElement;
  title: HTMLTitleElement;
  tr: HTMLTableRowElement;
  track: HTMLTrackElement;
  u: HTMLElement;
  ul: HTMLUListElement;
  var: HTMLElement;
  video: HTMLVideoElement;
  wbr: HTMLElement;
  webview: HTMLWebViewElement;
}

const elStringHTMLElementMap: {
  [K in keyof HTMLElements]: (
    props: React.HTMLProps<HTMLElements[K]>
  ) => JSX.Element;
} = {
  a: (props) => <a {...props} />,
  abbr: (props) => <abbr {...props} />,
  address: (props) => <address {...props} />,
  area: (props) => <area {...props} />,
  article: (props) => <article {...props} />,
  aside: (props) => <aside {...props} />,
  audio: (props) => <audio {...props} />,
  b: (props) => <b {...props} />,
  base: (props) => <base {...props} />,
  bdi: (props) => <bdi {...props} />,
  bdo: (props) => <bdo {...props} />,
  big: (props) => <big {...props} />,
  blockquote: (props) => <blockquote {...props} />,
  body: (props) => <body {...props} />,
  br: (props) => <br {...props} />,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  button: (props) => <button {...props} />,
  canvas: (props) => <canvas {...props} />,
  caption: (props) => <caption {...props} />,
  center: (props) => <center {...props} />,
  cite: (props) => <cite {...props} />,
  code: (props) => <code {...props} />,
  col: (props) => <col {...props} />,
  colgroup: (props) => <colgroup {...props} />,
  data: (props) => <data {...props} />,
  datalist: (props) => <datalist {...props} />,
  dd: (props) => <dd {...props} />,
  del: (props) => <del {...props} />,
  details: (props) => <details {...props} />,
  dfn: (props) => <dfn {...props} />,
  dialog: (props) => <dialog {...props} />,
  div: (props) => <div {...props} />,
  dl: (props) => <dl {...props} />,
  dt: (props) => <dt {...props} />,
  em: (props) => <em {...props} />,
  embed: (props) => <embed {...props} />,
  fieldset: (props) => <fieldset {...props} />,
  figcaption: (props) => <figcaption {...props} />,
  figure: (props) => <figure {...props} />,
  footer: (props) => <footer {...props} />,
  form: (props) => <form {...props} />,
  h1: (props) => <h1 {...props} />,
  h2: (props) => <h2 {...props} />,
  h3: (props) => <h3 {...props} />,
  h4: (props) => <h4 {...props} />,
  h5: (props) => <h5 {...props} />,
  h6: (props) => <h6 {...props} />,
  head: (props) => <head {...props} />,
  header: (props) => <header {...props} />,
  hgroup: (props) => <hgroup {...props} />,
  hr: (props) => <hr {...props} />,
  html: (props) => <html {...props} />,
  i: (props) => <i {...props} />,
  iframe: (props) => <iframe {...props} />,
  img: (props) => <img {...props} />,
  input: (props) => <input {...props} />,
  ins: (props) => <ins {...props} />,
  kbd: (props) => <kbd {...props} />,
  label: (props) => <label {...props} />,
  legend: (props) => <legend {...props} />,
  li: (props) => <li {...props} />,
  link: (props) => <link {...props} />,
  main: (props) => <main {...props} />,
  map: (props) => <map {...props} />,
  mark: (props) => <mark {...props} />,
  menu: (props) => <menu {...props} />,
  meta: (props) => <meta {...props} />,
  meter: (props) => <meter {...props} />,
  nav: (props) => <nav {...props} />,
  noindex: (props) => <noindex {...props} />,
  noscript: (props) => <noscript {...props} />,
  object: (props) => <object {...props} />,
  // AWD-2885. tiny type mismatch - string vs string literals
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  ol: (props) => <ol {...props} />,
  optgroup: (props) => <optgroup {...props} />,
  option: (props) => <option {...props} />,
  output: (props) => <output {...props} />,
  p: (props) => <p {...props} />,
  param: (props) => <param {...props} />,
  picture: (props) => <picture {...props} />,
  pre: (props) => <pre {...props} />,
  progress: (props) => <progress {...props} />,
  q: (props) => <q {...props} />,
  rp: (props) => <rp {...props} />,
  rt: (props) => <rt {...props} />,
  ruby: (props) => <ruby {...props} />,
  s: (props) => <s {...props} />,
  samp: (props) => <samp {...props} />,
  script: (props) => <script {...props} />,
  section: (props) => <section {...props} />,
  select: (props) => <select {...props} />,
  small: (props) => <small {...props} />,
  source: (props) => <source {...props} />,
  span: (props) => <span {...props} />,
  strong: (props) => <strong {...props} />,
  style: (props) => <style {...props} />,
  sub: (props) => <sub {...props} />,
  summary: (props) => <summary {...props} />,
  sup: (props) => <sup {...props} />,
  table: (props) => <table {...props} />,
  template: (props) => <template {...props} />,
  tbody: (props) => <tbody {...props} />,
  td: (props) => <td {...props} />,
  textarea: (props) => <textarea {...props} />,
  tfoot: (props) => <tfoot {...props} />,
  th: (props) => <th {...props} />,
  thead: (props) => <thead {...props} />,
  time: (props) => <time {...props} />,
  title: (props) => <title {...props} />,
  tr: (props) => <tr {...props} />,
  track: (props) => <track {...props} />,
  u: (props) => <u {...props} />,
  ul: (props) => <ul {...props} />,
  var: (props) => <var {...props} />,
  video: (props) => <video {...props} />,
  wbr: (props) => <wbr {...props} />,
  webview: (props) => <webview {...props} />,
  keygen: (props) => <keygen {...props} />,
  menuitem: (props) => <menuitem {...props} />,
  search: (props) => <search {...props} />,
  slot: (props) => <slot {...props} />,
};
